import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext } from "contexts/Brands";
import { MAX_CAMPAIGN_BRANDS, NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  BentoBrand,
  BentoBrandSearchParameters,
  MetadataType,
  SearchParams,
  Sort,
} from "schemas/dashboard";

import SourceStepFooter from "features/Influencer/Campaigns/NewCampaign/SharedComponents/SourceStepFooter";
import CategoryFilter from "features/Influencer/Discover/Results/CategoryFilter";
import MobileCategoryFilterDialog from "features/Influencer/Discover/Results/CategoryFilter/mobile";
import DiscoverFilter from "features/Influencer/Discover/Results/DiscoverFilter";
import DiscoverSearchBar from "features/Influencer/Discover/Search";
import { arrayQuery, fetcherAuth } from "utils/api";
import {
  convertFilterParamsToQuery,
  getCategoryIdParams,
  getMinAndMaxInstagram,
} from "utils/discoverSearch";
import { trackEvent } from "utils/tracking";
import { useSearchBrand } from "utils/useSearchBrand";

import Brands from "./Brands";

const RESULTS_PER_PAGE = 20;

export default function BrandSearch({
  repitching = false,
  savedBrandCollectionId,
  sourceCampaignId,
  isAllSavedCollection = false,
  nextLoading = false,
  onNext,
  onBack,
  secondaryAction,
  disableAutoScroll = false,
  sourceLabel,
}: {
  repitching?: boolean;
  savedBrandCollectionId?: number;
  sourceCampaignId?: number;
  isAllSavedCollection?: boolean;
  nextLoading?: boolean;
  onNext?: (
    converted?: BentoBrandSearchParameters,
    numberSelected?: number,
  ) => void;
  onBack?: () => void;
  secondaryAction?: React.ReactNode;
  disableAutoScroll?: boolean;
  sourceLabel?: string;
}) {
  const theme = useTheme();
  const { subscription } = useContext(SubscriptionContext);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const {
    selectAll,
    selectedBrandIds,
    setSelectAll,
    setSelectedBrandIds,
    excludedBrandIds,
    setExcludedBrandIds,
    setCurrentSearchParams,
    searchUrlParams,
    setSearchUrlParams,
  } = useContext(NewCampaignContext);
  const { topLevelCategories, categoriesById, categoriesLoading } = useContext(
    BentoCategoriesContext,
  );
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const { discoverFilterParams, selectedMetadataTags } =
    useContext(BrandsContext);
  const { getToken } = useAuth();
  const { clearAll } = useSearchBrand();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {
    selectedSort,
    selectedCategoryIds,
    selectedCompanyTypes,
    selectedContactTypes,
    selectedInstagramFollowings,
    selectedLocations,
    selectedGeopoints,
    selectedCompensationTypes,
    selectedLanguages,
    selectedTargetGenders,
  } = { ...discoverFilterParams };

  const [brands, setBrands] = useState<BentoBrand[]>([]);
  const abortController = useRef<AbortController | undefined>(undefined);
  const [cursor, setCursor] = useState<number[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [openMobileCategoryFilter, setOpenMobileCategoryFilter] =
    useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [ref, isBrandsRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);
  const hasActiveFilters = Boolean(
    brandQuery ||
      selectedCategoryIds?.length ||
      selectedCompanyTypes?.length ||
      selectedContactTypes?.length ||
      selectedInstagramFollowings?.length ||
      selectedLocations?.length ||
      selectedCompensationTypes?.length ||
      selectedLanguages?.length ||
      selectedTargetGenders?.length ||
      selectedMetadataTags?.length,
  );

  const [brandsLoading, setBrandsLoading] = useState<boolean>(true);
  const [fetchMoreLoading, setFetchMoreLoading] = useState<boolean>(false);
  const [hitsLimit, setHitsLimit] = useState<boolean>(false);
  const headRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const fetchBrands = async (
    searchQuery: string | null,
    fetchCursor: number[] | null,
    useFetchMoreLoading?: boolean,
    appendToList?: boolean,
  ) => {
    if (!currentOrg) {
      setBrandsLoading(false);
      return;
    }

    if (abortController?.current) {
      abortController.current?.abort();
    }
    let wasAborted = false;
    try {
      abortController.current = new AbortController();

      let query = searchQuery || "";
      if (selectedMetadataTags?.length > 0) {
        const clickedTags = selectedMetadataTags?.filter(
          (x) => x.type === MetadataType.tags,
        );
        query += clickedTags?.map((x) => x.value);
      }

      const encodedQuery = encodeURIComponent(query || "");

      let url = `/api/organization/${currentOrg?.id}/bento-brands?query=${encodedQuery}&size=${RESULTS_PER_PAGE}&contacts_required=true`;
      if (selectedSort) {
        url += `&sort=${selectedSort?.key}`;
      } else {
        url += `&sort=${Sort.Recommended}`;
      }

      if (selectedCategoryIds?.length > 0) {
        const categoryQueries = getCategoryIdParams(
          topLevelCategories,
          categoriesById,
          selectedCategoryIds,
        );
        url += `${arrayQuery(categoryQueries, "category_ids")}`;
      }

      if (selectedCompanyTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompanyTypes?.map((x) => x.key),
          "company_types",
        )}`;
      }

      if (selectedContactTypes?.length > 0) {
        url += `${arrayQuery(
          selectedContactTypes?.map((x) => x.key),
          "contact_types",
        )}`;
      }

      if (selectedCompensationTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompensationTypes?.map((x) => x.key),
          "compensation_types",
        )}`;
      }

      if (selectedLanguages?.length > 0) {
        url += `${arrayQuery(
          selectedLanguages?.map((x) => x.key),
          "languages",
        )}`;
      }

      if (selectedTargetGenders?.length > 0) {
        url += `${arrayQuery(
          selectedTargetGenders?.map((x) => x.key),
          "target_genders",
        )}`;
      }

      if (selectedLocations?.length > 0) {
        url += `${arrayQuery(
          selectedLocations?.map((x) => x.key),
          "locations",
        )}`;
      }

      if (selectedGeopoints?.length > 0) {
        url += `${arrayQuery(
          selectedGeopoints?.map((x) => x.key),
          "geopoints",
        )}`;
      }

      if (selectedInstagramFollowings?.length > 0) {
        const followers = getMinAndMaxInstagram(selectedInstagramFollowings);
        if (followers.min_instagram_followers) {
          url += `&min_instagram_followers=${followers.min_instagram_followers}`;
        }
        if (followers.max_instagram_followers) {
          url += `&max_instagram_followers=${followers.max_instagram_followers}`;
        }
      }
      if (fetchCursor) {
        url += `&cursor=${encodeURIComponent(JSON.stringify(fetchCursor))}`;
      }

      if (repitching) {
        url += "&is_repitching=true";
      }

      if (savedBrandCollectionId) {
        url += `&saved_brand_collection_id=${savedBrandCollectionId}`;
      }

      if (sourceCampaignId) {
        url += `&source_campaign_id=${sourceCampaignId}`;
      }

      if (isAllSavedCollection) {
        url += "&is_all_saved_collection=true";
      }

      if (useFetchMoreLoading) {
        setFetchMoreLoading(true);
      } else {
        setBrandsLoading(true);
      }
      const res = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
        {},
        false,
        false,
        true,
        abortController.current.signal,
      );

      if (res.bentoBrands.length < RESULTS_PER_PAGE) {
        setHitsLimit(true);
      } else {
        setHitsLimit(false);
      }

      if (appendToList) {
        setBrands((prevBrands: BentoBrand[]) => {
          const brandSet = new Set(prevBrands.map((brand) => brand.id));
          const newBrands = [...prevBrands];

          res.bentoBrands.forEach((brand: BentoBrand) => {
            if (!brandSet.has(brand.id)) {
              brandSet.add(brand.id);
              if (newBrands) {
                newBrands.push(brand);
              }
            }
          });
          return newBrands;
        });
      } else {
        setBrands([...res.bentoBrands]);
      }
      setCursor(res.cursor);
      setTotal(res.totalResults);
    } catch (error) {
      if (error?.message?.includes("aborted")) {
        wasAborted = true;
        return;
      }
      setAlert(
        error?.message ||
          "Something went wrong when fetching brands, please reload and try again",
        "error",
      );
    } finally {
      if (!wasAborted) {
        setBrandsLoading(false);
        setFetchMoreLoading(false);
      }
    }
  };

  const handleSearch = async () => {
    scrollToTop();
    setBrandsLoading(true);
    setCursor(null);
    setHitsLimit(false);
    fetchBrands(brandQuery, null, false, false);
  };

  const scrollToTop = () => {
    if (disableAutoScroll) return;
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    // Require an explicit selection — never silently fall back to "select all".
    if (numberSelected === 0) return;
    const converted = convertFilterParamsToQuery(
      topLevelCategories,
      categoriesById,
      discoverFilterParams,
      selectedMetadataTags,
      brandQuery || "",
    );
    if (savedBrandCollectionId) {
      converted.saved_brand_collection_id = savedBrandCollectionId;
    }
    if (sourceCampaignId) {
      converted.source_campaign_id = sourceCampaignId;
    }
    if (isAllSavedCollection) {
      converted.is_all_saved_collection = true;
    }
    setCurrentSearchParams(converted);
    setSearchUrlParams(location.search);
    trackEvent("New Campaign Dialog Selected Brand Search Brands", {
      "Select All": selectAll,
      "Number Of Brands Selected": selectedBrandIds.size,
      "Number Of Brands Excluded": excludedBrandIds.size,
      "Search Params": location.search,
      "Saved Brand Collection ID": savedBrandCollectionId,
      "Is All Saved Collection": isAllSavedCollection,
    });

    if (onNext) {
      onNext(converted, numberSelected);
    }
  };

  const handleBack = () => {
    setSelectAll(false);
    setSelectedBrandIds(new Set());
    setExcludedBrandIds(new Set());
    // Reset filters/search so re-entering the brand search starts fresh.
    clearAll();
    setSearchUrlParams("");
    setCurrentSearchParams({ query: "", sort: Sort.Recommended });
    if (onBack) {
      onBack();
    }
  };

  useEffect(() => {
    const isElementInView = () => {
      const rect = topRef?.current?.getBoundingClientRect();
      if (!rect) {
        return false;
      }
      return rect.top >= 0 && rect.bottom <= (window.innerHeight || 0);
    };

    // Scroll to the top of the search (the search bar), not below it.
    if (!disableAutoScroll && topRef.current && !isElementInView()) {
      topRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "start",
      });
    }
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(discoverFilterParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedMetadataTags?.length,
    brandQuery,
    currentOrg?.id,
    savedBrandCollectionId,
    sourceCampaignId,
    isAllSavedCollection,
  ]);

  useEffect(
    () => () => {
      if (abortController.current) {
        abortController.current?.abort();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (searchUrlParams && !location.search) {
      navigate(`${searchUrlParams}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isBrandsRefVisible) {
      if (!hitsLimit && !brandsLoading) {
        fetchBrands(brandQuery, cursor, true, true);
        trackEvent("Campaigns Brand Search Page Scrolled To Bottom", {
          keyword: brandQuery,
          categoryIds: selectedCategoryIds,
          "Company Type": selectedCompanyTypes,
          "Contact Type": selectedContactTypes?.map((x) => x.key),
          sort: selectedSort,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrandsRefVisible, brandsLoading]);

  const numberSelected = selectAll
    ? Math.max(
        0,
        Math.min(total, subscription?.remainingMessages!, MAX_CAMPAIGN_BRANDS) -
          excludedBrandIds.size,
      )
    : selectedBrandIds.size;

  return (
    <>
      <Box ref={topRef} />
      {/* Search + filters — stay fixed above the scrolling results */}
      <Box>
        <DiscoverSearchBar alwaysShow />
        <DiscoverFilter newCampaignView />
      </Box>

      {/* Selection status + select-all */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          my: 1,
          bgcolor: "rgba(1,67,57,0.04)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-check"
            sx={{
              fontSize: 15,
              flexShrink: 0,
              color: numberSelected > 0 ? "primary.main" : "text.disabled",
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
            {numberSelected > 0
              ? `${numberSelected} selected`
              : isMobileScreen
                ? "Select brands"
                : "Select brands to continue"}
          </Typography>
          {!isMobileScreen && total > 0 && (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
              · {total === 10000 ? "10,000+" : total} result
              {total === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          sx={{ m: 0, flexShrink: 0 }}
          control={
            <Checkbox
              size="small"
              checked={selectAll}
              disableRipple
              onChange={() => {
                setSelectAll(!selectAll);
                setSelectedBrandIds(new Set());
                setExcludedBrandIds(new Set());
              }}
              sx={{
                color: "primary.main",
                "&.Mui-checked": { color: "primary.main" },
              }}
            />
          }
          label={<Box sx={{ fontSize: 13, fontWeight: 600 }}>Select all</Box>}
          labelPlacement="start"
        />
      </Box>

      {/* Sticky category sidebar + scrolling results */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {!isMobileScreen && (
          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              alignSelf: "flex-start",
              position: "sticky",
              top: 0,
              maxHeight: "calc(100vh - 380px)",
              overflowY: "auto",
            }}
          >
            <CategoryFilter topLevelCategories={topLevelCategories} />
          </Box>
        )}
        <Box
          ref={headRef}
          sx={{
            flex: 1,
            minWidth: 0,
            height: "calc(100vh - 380px)",
            minHeight: 360,
            overflowY: "auto",
          }}
        >
          <div ref={resultsRef} />
          <Brands
            brands={brands}
            brandsLoading={brandsLoading || categoriesLoading}
            fetchMoreLoading={fetchMoreLoading}
            ref={ref}
            numberSelected={numberSelected}
            total={total}
            isRepitching={repitching}
            hasActiveFilters={hasActiveFilters}
            sourceLabel={sourceLabel}
          />
        </Box>
      </Box>

      {isMobileScreen && (
        <MobileCategoryFilterDialog
          topLevelCategories={topLevelCategories}
          openMobileCategoryFilter={openMobileCategoryFilter}
          setOpenMobileCategoryFilter={setOpenMobileCategoryFilter}
        />
      )}

      <SourceStepFooter
        onBack={handleBack}
        onNext={handleNext}
        nextDisabled={numberSelected === 0}
        nextLoading={nextLoading}
        secondaryAction={secondaryAction}
      />
    </>
  );
}
